import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wardAPI } from '../services/api';

interface Ward {
  id: string;
  name: string;
  county: string;
}

const VolunteerApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    national_id: '',
    date_of_birth: '',
    gender: '',
    ward_id: '',
    sub_location: '',
    residence_address: '',
    education_level: '',
    occupation: '',
    previous_volunteer_experience: '',
    skills_and_expertise: '',
    languages_spoken: '',
    motivation: '',
    availability_hours_per_week: '',
    preferred_activities: ''
  });

  useEffect(() => {
    loadWards();
  }, []);

  const loadWards = async () => {
    try {
      const response = await wardAPI.getByCounty('Turkana');
      setWards(response.data);
    } catch (error) {
      console.error('Error loading wards:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/volunteer/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Application submitted successfully! Ward officers will review your application.');
        setFormData({
          full_name: '',
          email: '',
          phone_number: '',
          national_id: '',
          date_of_birth: '',
          gender: '',
          ward_id: '',
          sub_location: '',
          residence_address: '',
          education_level: '',
          occupation: '',
          previous_volunteer_experience: '',
          skills_and_expertise: '',
          languages_spoken: '',
          motivation: '',
          availability_hours_per_week: '',
          preferred_activities: ''
        });
      } else {
        setMessage(data.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Application error:', error);
      setMessage('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="volunteer-application-page">
      <header className="page-header">
        <div className="icon icon-primary">🤝</div>
        <h1>Community Volunteer Application</h1>
        <p>Join our community volunteer program and help document community engagements</p>
      </header>

      {message && (
        <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="application-info">
        <div className="info-section">
          <h3>About Community Volunteers</h3>
          <p>Community volunteers help bridge the gap between county services and citizens by:</p>
          <ul>
            <li>📚 Sharing educational materials and resources</li>
            <li>🤝 Documenting community engagements and activities</li>
            <li>📸 Capturing community feedback and testimonials</li>
            <li>🔄 Facilitating communication between citizens and officials</li>
          </ul>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="volunteer-form">
        <div className="form-section">
          <h3>Personal Information</h3>
          
          <div className="form-row">
            <input
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              placeholder="Full Name *"
              required
            />
            <input
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              placeholder="Phone Number *"
              required
            />
          </div>

          <div className="form-row">
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email Address (Optional)"
            />
            <input
              name="national_id"
              value={formData.national_id}
              onChange={handleInputChange}
              placeholder="National ID Number *"
              required
            />
          </div>

          <div className="form-row">
            <input
              name="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={handleInputChange}
              placeholder="Date of Birth"
            />
            <select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Location Information</h3>
          
          <div className="form-row">
            <select
              name="ward_id"
              value={formData.ward_id}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Ward *</option>
              {wards.map(ward => (
                <option key={ward.id} value={ward.id}>{ward.name}</option>
              ))}
            </select>
            <input
              name="sub_location"
              value={formData.sub_location}
              onChange={handleInputChange}
              placeholder="Sub-location/Village"
            />
          </div>

          <div className="form-row">
            <textarea
              name="residence_address"
              value={formData.residence_address}
              onChange={handleInputChange}
              placeholder="Residence Address"
              rows={2}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Background Information</h3>
          
          <div className="form-row">
            <select
              name="education_level"
              value={formData.education_level}
              onChange={handleInputChange}
            >
              <option value="">Select Education Level</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="tertiary">Tertiary/College</option>
              <option value="university">University</option>
              <option value="postgraduate">Postgraduate</option>
            </select>
            <input
              name="occupation"
              value={formData.occupation}
              onChange={handleInputChange}
              placeholder="Current Occupation"
            />
          </div>

          <div className="form-row">
            <textarea
              name="previous_volunteer_experience"
              value={formData.previous_volunteer_experience}
              onChange={handleInputChange}
              placeholder="Previous volunteer experience (if any)"
              rows={3}
            />
          </div>

          <div className="form-row">
            <textarea
              name="skills_and_expertise"
              value={formData.skills_and_expertise}
              onChange={handleInputChange}
              placeholder="Skills and areas of expertise"
              rows={3}
            />
          </div>

          <div className="form-row">
            <input
              name="languages_spoken"
              value={formData.languages_spoken}
              onChange={handleInputChange}
              placeholder="Languages spoken (e.g., English, Kiswahili, Turkana)"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Motivation and Availability</h3>
          
          <div className="form-row">
            <textarea
              name="motivation"
              value={formData.motivation}
              onChange={handleInputChange}
              placeholder="Why do you want to become a community volunteer? *"
              rows={4}
              required
            />
          </div>

          <div className="form-row">
            <input
              name="availability_hours_per_week"
              type="number"
              value={formData.availability_hours_per_week}
              onChange={handleInputChange}
              placeholder="Available hours per week"
              min="1"
              max="40"
            />
          </div>

          <div className="form-row">
            <textarea
              name="preferred_activities"
              value={formData.preferred_activities}
              onChange={handleInputChange}
              placeholder="Preferred volunteer activities (e.g., community meetings, training sessions, material distribution)"
              rows={3}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/login')} className="btn btn-secondary">
            Back to Login
          </button>
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>

      <div className="application-note">
        <h4>What happens next?</h4>
        <ol>
          <li>Your application will be reviewed by civic moderators</li>
          <li>You may be contacted for an interview</li>
          <li>If approved, you'll receive login credentials</li>
          <li>You'll get access to the volunteer dashboard</li>
        </ol>
      </div>
    </div>
  );
};

export default VolunteerApplicationPage;